import { motion } from 'framer-motion'
import moment from 'moment'
import Papa from 'papaparse'
import { useEffect, useState } from 'react'

import { usePortalSearchUnits } from '@apps-common/apis/portal/unit'
import { InfiniteLoader } from '@apps-common/components/common'
import { ButtonRoundedSm, CheckboxInputSM } from '@apps-common/components/elements'
import { ModalBody, ModalCloseButton, ModalFooter, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { UNIT_STATUS_CANCELLED, UNIT_STATUS_INACTIVE, UNIT_STATUS_PENDING_CANCELLATION } from '@apps-common/config/unit-filter'
import { portalUnitFilterReport } from '@apps-common/config/unit-reports'
import { toastSuccess } from '@apps-common/lib/toast'
import { usePortalUnitsFilterStore } from '@apps-common/stores'
import { IPortalUnitFilterDefaults } from '@apps-common/types/portal/unit'
import { FilterModalContainer } from './filter/FilterModalContainer'

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => string
  }
}

export interface IPortalUnitsFilterModal {
  defaults?: IPortalUnitFilterDefaults
}

export const PortalUnitsFilterExportModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IPortalUnitsFilterModal
}) => {
  const state = usePortalUnitsFilterStore()
  const [keys, setKeys] = useState<{ label: string; key: string; checked: boolean }[]>([])
  const [enabled, setEnabled] = useState<boolean>(false)
  const { isLoading, isFetching, isError, data } = usePortalSearchUnits({
    config: {
      enabled
    },
    model: {
      ...state,
      skip: 0,
      take: 1000000
    }
  })

  useEffect(() => {
    if (state.status === UNIT_STATUS_INACTIVE.value) {
      setKeys(portalUnitFilterReport.inactive.map((k) => ({ label: k.label, key: k.key, checked: true })))
    } else if (state.status === UNIT_STATUS_PENDING_CANCELLATION.value) {
      setKeys(portalUnitFilterReport.pendingCancellation.map((k) => ({ label: k.label, key: k.key, checked: true })))
    } else if (state.status === UNIT_STATUS_CANCELLED.value) {
      setKeys(portalUnitFilterReport.cancelled.map((k) => ({ label: k.label, key: k.key, checked: true })))
    } else {
      setKeys(portalUnitFilterReport.active.map((k) => ({ label: k.label, key: k.key, checked: true })))
    }
  }, [])

  const downloadExportCSV = () => {
    let csv = Papa.unparse({ fields: keys.filter((k) => k.checked).map((k) => k.key), data: data || [] })

    for (let i = 0; i < keys.length; i++) {
      csv = csv.replaceAll(keys[i].key, keys[i].label)
    }

    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    let csvURL = null

    if (navigator.msSaveBlob) {
      csvURL = navigator.msSaveBlob(csvData, 'download.csv')
    } else {
      csvURL = window.URL.createObjectURL(csvData)
    }

    const tempLink = document.createElement('a')

    tempLink.href = csvURL
    tempLink.setAttribute('download', `radionet_portal_devices_export_${moment().format('DD.MM.YY.hh.mm')}.csv`)
    tempLink.click()

    onDismiss(null, ModalDismiss.Confirm)
    toastSuccess('Export complete.')
  }

  useEffect(() => {
    if (data) {
      downloadExportCSV()
    }
  }, [data])

  const nonSelected = !keys.some((k) => k.checked)
  const loading = enabled || isLoading || isFetching || isError

  return (
    <FilterModalContainer onDismiss={onDismiss} classes='sm:!max-w-[500px] sm:!h-auto' useFullHeight>
      {loading && (
        <motion.div
          key='export-loading-fad-in'
          {...motionFadeIn(0.25)}
          className='absolute top-0 left-0 z-[10] w-full h-full flex flex-col items-center justify-center gap-4 bg-surface/30 backdrop-blur-md text-text px-8 text-center'
        >
          <div className='jost-medium'>Exporting your devices...</div>
          Please be patient. Depending on the export size, this could take some time...
          <InfiniteLoader key='export-loader' loading />
        </motion.div>
      )}
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Devices Export' title='Field Selection' titleClasses='flex flex-col' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
        <button
          type='button'
          aria-label='Toggle all'
          className='jost pt-2 text-[0.8em] underline text-text'
          onClick={() => {
            if (!nonSelected) {
              setKeys(keys.map((k) => ({ ...k, checked: false })))
            } else {
              setKeys(keys.map((k) => ({ ...k, checked: true })))
            }
          }}
        >
          Toggle all
        </button>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[120px] !pb-[80px]'>
        <div className='grid sm:grid-cols-2 gap-2 sm:gap-3'>
          {keys?.map((k, i) => (
            <div className='bg-surface px-2 pt-1 rounded-xl'>
              <CheckboxInputSM
                label={k.label}
                id={`export-${k}`}
                key={`export-${k}`}
                disabled={loading}
                registration={{
                  checked: k.checked,
                  onClick: (e: any) => {
                    const cloned = [...keys]

                    cloned[i] = { ...k, checked: e?.target?.checked }
                    setKeys(cloned)
                  }
                }}
              />
            </div>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            type='button'
            loading={loading || nonSelected}
            text='Export'
            className='action-success'
            callback={() => setEnabled(true)}
          />
          <ButtonRoundedSm
            type='button'
            loading={loading}
            text='Cancel'
            className='action-neutral'
            callback={() => onDismiss(null, 'cancel')}
          />
        </div>
      </ModalFooter>
    </FilterModalContainer>
  )
}
